import { createContext, useState, useEffect } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import Honeybadger from '@honeybadger-io/js';
import crazyAi from '@crazyegginc/ai';

import { isE2E, isStaging } from '/src/utils';
import { getParam } from '/src/utils/location';

export const CrazyAIContext = createContext();
export const CrazyAIQueryContext = createContext();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false,
      useErrorBoundary: false,
      staleTime: 30 * 60 * 1000,
      queryFn: () => new Promise(() => {}),
    },
  },
});

export function CrazyAIProvider({ children }) {
  const e2e = isE2E();
  const [conn, setConn] = useState(null);
  const stagingFlag = isStaging() || getParam('staging') === 'true';

  useEffect(() => {
    let canceled = false;
    let c;
    async function connect() {
      c = await crazyAi.connection({
        version: 'v2',
        env: stagingFlag ? 'stag' : 'prod',
        onError: (error) => {
          Honeybadger.notify('CrazyAI error', {
            context: {
              error,
            },
            name: 'CrazyAI error - new',
          });
        },
      });
      if (!canceled && c) {
        setConn(c);
      } else {
        c?.close();
      }
    }
    if (!conn && !canceled && !e2e && !import.meta.env.VITEST) {
      connect();
    }

    return () => {
      canceled = true;
    };
  }, [conn, e2e, stagingFlag]);

  return (
    <CrazyAIContext.Provider value={{ ai: conn }}>
      <QueryClientProvider client={queryClient} context={CrazyAIQueryContext}>
        {children}
      </QueryClientProvider>
    </CrazyAIContext.Provider>
  );
}
